import React, { useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { TaskyButton, TaskyInput, TaskyTextarea } from '../../../components/tasky';
import {
  DcmAppeal,
  DcmDemo,
  DcmEmotionMap,
  DcmSegmentMaster,
  DcmValue,
  DcmWho,
  FUNNEL_STAGE_OPTIONS,
} from '../../../types/knowledge';
import {
  AppealContent,
  buildAppealFromContent,
  generateCopyContent,
} from '../../../services/api/knowledgeStrategyGeneration';
import AppealGenerateCard from './AppealGenerateCard';
import AppealGenerateModal from './AppealGenerateModal';

interface PersonaDrawerProps {
  who: DcmWho | null;
  productId: number;
  segments: DcmSegmentMaster[];
  onClose: () => void;
  onSave: (who: DcmWho) => void;
  onDelete: (whoId: string) => void;
}

const CARD_SHADOW = '0 0 18px 0 rgba(31,23,18,.06)';

/**
 * 訴求の個別編集: 保存前の最低限のバリデーション（空文字保存のガード）。
 * - 訴求名が空欄なら仮の名前にフォールバックする（保存をブロックせず、常に有効な状態にする）。
 * - 空文字のコピー案は取り除く。全件空になった場合は入力を促すプレースホルダー1件を残す。
 * ブロッキングのエラーUIは出さず、既存コード（buildAppeal等）と同じ「常に非空データを保証する」方針に揃える。
 */
const normalizeAppealsForSave = (appeals: DcmAppeal[]): DcmAppeal[] =>
  appeals.map((appeal, index) => {
    const trimmedName = appeal.name.trim();
    const nonEmptyCopies = appeal.copies
      .map((copy) => ({ ...copy, text: copy.text.trim() }))
      .filter((copy) => copy.text.length > 0);

    return {
      ...appeal,
      name: trimmedName || `訴求${index + 1}`,
      copies:
        nonEmptyCopies.length > 0
          ? nonEmptyCopies
          : [{ id: nanoid(), text: 'コピー案を入力してください。', use: true }],
    };
  });

// DCMモック(02 §4.2) 準拠のペルソナ属性6項目（居住/性別/職業・業界/購買力/ITリテラシー/ライフステージ）。
const DEMO_FIELDS: Array<{ label: string; key: keyof DcmDemo }> = [
  { label: '居住', key: 'region' },
  { label: '性別', key: 'gender' },
  { label: '職業・業界', key: 'occupation' },
  { label: '購買力', key: 'purchasing_power' },
  { label: 'ITリテラシー', key: 'tech_literacy' },
  { label: 'ライフステージ', key: 'life_stage' },
];

// インサイト（感情マップ）4項目。
const INSIGHT_FIELDS: Array<{ label: string; key: keyof DcmEmotionMap }> = [
  { label: '課題への感情', key: 'pain_emotion' },
  { label: '理想の状態', key: 'ideal_state' },
  { label: '障害となる感情', key: 'blocking_emotion' },
  { label: '購買決定要因', key: 'decision_factor' },
];

// VALUE 4分類（機能/経済/情緒/社会）。
const VALUE_ROWS: Array<{ label: string; key: keyof DcmValue; color: string; icon: string }> = [
  { label: '機能的価値', key: 'functional_value', color: '#616FC9', icon: 'bolt' },
  { label: '経済的価値', key: 'economic_value', color: '#4F86C6', icon: 'savings' },
  { label: '情緒的価値', key: 'emotional_value', color: '#C9619C', icon: 'favorite' },
  { label: '社会的価値', key: 'social_value', color: '#44C2A4', icon: 'groups' },
];

const PersonaDrawer: React.FC<PersonaDrawerProps> = ({
  who,
  productId,
  segments,
  onClose,
  onSave,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<DcmWho | null>(who);
  const [selectedAppealId, setSelectedAppealId] = useState<string | null>(null);
  // DCMモック(02 §4.2)準拠: ペルソナ理解は既定で要約のみ（トグルで展開。編集中は常に展開）
  const [personaExpanded, setPersonaExpanded] = useState(false);
  const whoIdRef = useRef<string | null>(null);
  // 訴求追加モーダル（セグメント/ペルソナ追加と同型。ローディングはモーダル側）
  const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
  // 「使わない案を再生成」中のコピーID集合（対象の全行にローディングを表示）
  const [regeneratingCopyIds, setRegeneratingCopyIds] = useState<Set<string>>(new Set());
  // 訴求単位の二重発火防止（setState は非同期なため、クリック直後の連打をrefで同期的に防ぐ）
  const regeneratingAppealIdsRef = useRef<Set<string>>(new Set());
  const genTimersRef = useRef<{ interval: number | null; timeouts: number[] }>({ interval: null, timeouts: [] });
  const appealsListRef = useRef<HTMLDivElement | null>(null);

  const clearGenTimers = () => {
    const timers = genTimersRef.current;
    if (timers.interval !== null) window.clearInterval(timers.interval);
    timers.interval = null;
    timers.timeouts.forEach((id) => window.clearTimeout(id));
    timers.timeouts = [];
  };

  // アンマウント時にタイマーを必ず破棄（生成途中でドロワーを閉じても leak しない）
  useEffect(() => clearGenTimers, []);

  useEffect(() => {
    // 別ペルソナに切り替わったときだけ編集状態・選択訴求をリセットする。
    // 同一ペルソナの再保存（コピー採否トグル等）では選択・スクロールを保持。
    const switched = (who?.who_id ?? null) !== whoIdRef.current;
    whoIdRef.current = who?.who_id ?? null;
    setDraft(who);
    if (switched) {
      setIsEditing(false);
      setPersonaExpanded(false);
      setSelectedAppealId(who?.appeals?.[0]?.appeal_id || null);
      // 切り替わったらスクロール予約を破棄し、開いていた訴求追加モーダルも閉じる
      clearGenTimers();
      setIsAppealModalOpen(false);
    }
  }, [who]);

  if (!who || !draft) {
    return null;
  }

  const segment = segments.find((item) => item.segment_id === draft.segment_id);
  const funnel = FUNNEL_STAGE_OPTIONS.find((item) => item.value === draft.funnel_stage_id);

  const updateDraft = (updater: (current: DcmWho) => DcmWho) => {
    setDraft((current) => (current ? updater(current) : current));
  };

  const mapAppeals = (
    current: DcmWho,
    appealId: string,
    updater: (appeal: DcmAppeal) => DcmAppeal,
  ): DcmWho => ({
    ...current,
    appeals: current.appeals.map((appeal) => (appeal.appeal_id === appealId ? updater(appeal) : appeal)),
  });

  // 訴求を追加: セグメント/ペルソナ追加と同じ生成モーダル（AIにおまかせ／指示を出して生成）を開く。
  // ローディング演出はモーダル側（AppealGenerateModal）が担い、完了時に appendAppeal が呼ばれる。
  const handleAddAppeal = () => {
    setIsAppealModalOpen(true);
  };

  // モーダルの生成完了時に訴求を追加して新カードへスクロール。
  // 生成された中身（AppealContent）を型付きに組み立てて末尾に追加し、
  // 編集モード外なら即座に永続化する（コピー採否トグルと同じ挙動）。
  const appendAppeal = (content: AppealContent) => {
    const appeal: DcmAppeal = buildAppealFromContent(content, draft.appeals.length);
    const next: DcmWho = { ...draft, appeals: [...draft.appeals, appeal] };
    setDraft(next);
    setSelectedAppealId(appeal.appeal_id);
    if (!isEditing) {
      onSave(next);
    }
    // 追加は末尾 push なので、描画後に列内の最後の訴求カードへスクロール
    genTimersRef.current.timeouts.push(
      window.setTimeout(() => {
        const cards = appealsListRef.current?.querySelectorAll('[data-testid="appeal-card"]');
        cards?.[cards.length - 1]?.scrollIntoView({ block: 'center' });
      }, 30),
    );
  };

  // コピー採否トグル（DCMモック準拠: 編集モード外でも即操作可）。
  // 編集中は draft を更新し保存ボタンで確定。非編集時は onSave 経由で即保存（updateAndSave）。
  const handleToggleCopy = (appealId: string, copyId: string) => {
    const next = mapAppeals(draft, appealId, (appeal) => ({
      ...appeal,
      copies: appeal.copies.map((copy) => (copy.id === copyId ? { ...copy, use: !copy.use } : copy)),
    }));
    setDraft(next);
    if (!isEditing) {
      onSave(next);
    }
  };

  const handleEditCopy = (appealId: string, copyId: string, text: string) => {
    updateDraft((current) =>
      mapAppeals(current, appealId, (appeal) => ({
        ...appeal,
        copies: appeal.copies.map((copy) => (copy.id === copyId ? { ...copy, text } : copy)),
      })),
    );
  };

  const handleDeleteCopy = (appealId: string, copyId: string) => {
    updateDraft((current) =>
      mapAppeals(current, appealId, (appeal) => ({
        ...appeal,
        copies: appeal.copies.filter((copy) => copy.id !== copyId),
      })),
    );
  };

  // 訴求の個別編集: 訴求名・訴求タイプ・訴求軸・狙いを1フィールドずつ書き換える。
  // 編集モード中のみ呼ばれる想定のため draft を更新するだけで、永続化は「保存」ボタン側で行う。
  const handleEditAppealField = (
    appealId: string,
    field: 'name' | 'type' | 'appeal_axis' | 'direction',
    value: string,
  ) => {
    updateDraft((current) =>
      mapAppeals(current, appealId, (appeal) => ({
        ...appeal,
        [field]: value,
      })),
    );
  };

  // コピー案を1件追加する（空文字で追加し、その場でcontentEditable編集させる）。
  const handleAddCopy = (appealId: string) => {
    updateDraft((current) =>
      mapAppeals(current, appealId, (appeal) => ({
        ...appeal,
        copies: [...appeal.copies, { id: nanoid(), text: '', use: true }],
      })),
    );
  };

  // 「使わない案を再生成」: 「使う」に選択されていない（=使わない）コピーを全件、
  // AI（失敗時テンプレ）で新しい案に置き換える。「使う」済みのコピーは変更しない。
  // 生成済み・既存コピーを都度 existing_copies に積み増して渡し、別角度（重複回避）を担保する。
  // ローディング表示は対象の全行に出し、完了後は編集モード外なら即座に一括永続化する。
  const handleRegenerateUnusedCopies = async (appealId: string) => {
    if (regeneratingAppealIdsRef.current.has(appealId)) return;
    const targetAppeal = draft.appeals.find((appeal) => appeal.appeal_id === appealId);
    const targets = targetAppeal?.copies.filter((copy) => !copy.use) ?? [];
    if (targets.length === 0) return;

    regeneratingAppealIdsRef.current.add(appealId);
    setRegeneratingCopyIds((current) => {
      const next = new Set(current);
      targets.forEach((copy) => next.add(copy.id));
      return next;
    });

    const existingCopies = targetAppeal!.copies.map((copy) => copy.text);
    try {
      let working = draft;
      for (const target of targets) {
        const appealNow = working.appeals.find((appeal) => appeal.appeal_id === appealId);
        const content = await generateCopyContent(productId, {
          persona_name: draft.who_name,
          appeal_name: appealNow?.name,
          appeal_direction: appealNow?.direction,
          existing_copies: existingCopies,
        });
        const newText = content.text?.trim() || `${draft.who_name}向けの新しいコピー案。`;
        existingCopies.push(newText);
        working = mapAppeals(working, appealId, (appeal) => ({
          ...appeal,
          // use 状態は維持（「再生成する案」のまま本文だけ差し替え）
          copies: appeal.copies.map((copy) => (copy.id === target.id ? { ...copy, text: newText } : copy)),
        }));
        setDraft(working);
      }
      if (!isEditing) {
        onSave(working);
      }
    } finally {
      regeneratingAppealIdsRef.current.delete(appealId);
      setRegeneratingCopyIds((current) => {
        const next = new Set(current);
        targets.forEach((copy) => next.delete(copy.id));
        return next;
      });
    }
  };

  const handleDeleteAppeal = (appealId: string) => {
    updateDraft((current) => ({
      ...current,
      appeals: current.appeals.filter((appeal) => appeal.appeal_id !== appealId),
    }));
    if (selectedAppealId === appealId) {
      const remaining = draft.appeals.filter((appeal) => appeal.appeal_id !== appealId);
      setSelectedAppealId(remaining[0]?.appeal_id || null);
    }
  };

  const selectedAppeal =
    draft.appeals.find((appeal) => appeal.appeal_id === selectedAppealId) || draft.appeals[0];

  return (
    // ⑦: オーバーレイ（ドロワー外）クリックで閉じる。ドロワー本体へのクリックは
    // バブリングで backdrop まで届くため、target===currentTarget（backdrop 自身への
    // クリック）の時だけ閉じる。編集中でも×ボタンと同じ挙動（確認なしでそのまま閉じる）。
    <div
      data-testid="persona-drawer-backdrop"
      className="fixed inset-0 z-[55] flex justify-end bg-[rgba(20,18,28,0.28)]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="flex h-full w-[min(780px,94vw)] flex-col bg-[#f8f7f3] shadow-[-14px_0_44px_rgba(20,18,28,0.18)] max-[900px]:w-screen"
        aria-label="ペルソナ詳細"
      >
        <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-warm-border bg-white px-5 py-[13px]">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="inline-flex h-[34px] w-[34px] flex-[0_0_auto] items-center justify-center rounded-full bg-[linear-gradient(135deg,#616fc9,#9675c9)] text-sm font-extrabold text-white">
              {draft.who_name.slice(0, 1)}
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-gray-500">
                {draft.who_name} ・ {segment?.segment_name || 'セグメント'} × {funnel?.label || 'ファネル'}
              </div>
              {isEditing ? (
                <TaskyInput
                  value={draft.who_name}
                  onChange={(event) => updateDraft((current) => ({ ...current, who_name: event.target.value }))}
                  className="mt-2"
                />
              ) : (
                <h2 className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-[17px] font-extrabold leading-[1.3] text-dark">
                  {draft.who_name}
                </h2>
              )}
            </div>
          </div>
          {/* ml-auto で操作ボタン群を右端へ（×は常に右上端） */}
          <div className="ml-auto flex flex-shrink-0 items-center gap-2">
            {isEditing ? (
              <>
                <span className="rounded-full bg-warning/15 px-2 py-1 text-xs text-warning">編集中</span>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-[5px] rounded-lg border px-2.5 py-[7px] text-xs font-bold transition-[background,border-color,color] duration-[120ms] border-primary bg-primary text-white hover:border-primary-hover hover:bg-primary-hover hover:text-white"
                  onClick={() => {
                    // 空文字保存のガード: 訴求名・コピー案の空欄を保存直前に正規化してから永続化する。
                    const normalized: DcmWho = { ...draft, appeals: normalizeAppealsForSave(draft.appeals) };
                    setDraft(normalized);
                    onSave(normalized);
                    setIsEditing(false);
                  }}
                >
                  <Check className="mr-2 h-4 w-4" />
                  保存
                </button>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-[5px] rounded-lg border px-2.5 py-[7px] text-xs font-bold transition-[background,border-color,color] duration-[120ms] border-border bg-white text-gray-600 hover:border-[#aeb5de] hover:bg-[#f5f6fc] hover:text-primary-hover"
                  onClick={() => { setDraft(who); setIsEditing(false); }}
                >
                  キャンセル
                </button>
              </>
            ) : (
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-[5px] rounded-lg border px-2.5 py-[7px] text-xs font-bold transition-[background,border-color,color] duration-[120ms] border-border bg-white text-gray-600 hover:border-[#aeb5de] hover:bg-[#f5f6fc] hover:text-primary-hover"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                編集
              </button>
            )}
            {!isEditing && (
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-[5px] rounded-lg border px-2.5 py-[7px] text-xs font-bold transition-[background,border-color,color] duration-[120ms] border-border bg-white text-destructive hover:border-[#aeb5de] hover:bg-[#f5f6fc] hover:text-primary-hover"
                onClick={() => onDelete(draft.who_id)}
                aria-label="削除"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              className="cursor-pointer rounded-lg border-none bg-transparent p-1.5 text-gray-400 transition-[background,color] duration-[120ms] hover:bg-warm-surface hover:text-dark"
              onClick={onClose}
              aria-label="閉じる"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="persona-drawer-body flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          {/* ===== ペルソナ理解（折りたたみ: 要約 → 展開で詳細） ===== */}
          {/* flex-shrink-0: 縦flexのスクロール body 内で overflow-hidden カードが高さ0に潰れるのを防ぐ */}
          <div className="flex-shrink-0 overflow-hidden rounded-2xl bg-white" style={{ boxShadow: CARD_SHADOW }}>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-[18px] py-[13px] text-left transition-colors hover:bg-[#FAFAF8]"
              onClick={() => {
                if (!isEditing) setPersonaExpanded((value) => !value);
              }}
            >
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-purple text-white">
                <span className="material-icons-round text-[24px]">person</span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="text-[8.5px] font-bold text-primary" style={{ letterSpacing: '.1em' }}>
                    ペルソナ
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {segment?.segment_name || 'セグメント'} × {funnel?.label || 'ファネル'}
                  </span>
                </span>
                <span className="block truncate text-[15px] font-bold text-dark">
                  {draft.persona?.name || draft.who_name}
                  {draft.persona?.age && (
                    <span className="ml-1.5 text-[11px] font-normal text-gray-400">{draft.persona.age}</span>
                  )}
                </span>
                {draft.persona?.tagline && (
                  <span className="block truncate text-[10.5px] text-gray-400">{draft.persona.tagline}</span>
                )}
              </span>
              <span className="flex-shrink-0 text-[10px] font-semibold text-gray-400">
                {personaExpanded || isEditing ? '閉じる' : '詳細'}
              </span>
              <span className="material-icons-round flex-shrink-0 text-[22px] text-gray-400">
                {personaExpanded || isEditing ? 'expand_less' : 'expand_more'}
              </span>
            </button>

            {(personaExpanded || isEditing) && (
            <div className="border-t border-warm-border px-5 pb-5 pt-4">
              {/* 代表人物（編集時のみ入力欄を出す） */}
              {isEditing && (
                <div className="mb-3.5 grid grid-cols-3 gap-x-4 gap-y-2.5">
                  <div>
                    <div className="text-[10px] text-gray-400">名前</div>
                    <TaskyInput
                      value={draft.persona?.name || ''}
                      className="mt-0.5"
                      onChange={(event) => updateDraft((current) => ({ ...current, persona: { ...current.persona, name: event.target.value } }))}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400">年齢</div>
                    <TaskyInput
                      value={draft.persona?.age || ''}
                      className="mt-0.5"
                      onChange={(event) => updateDraft((current) => ({ ...current, persona: { ...current.persona, age: event.target.value } }))}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400">プロフィール</div>
                    <TaskyInput
                      value={draft.persona?.tagline || ''}
                      className="mt-0.5"
                      onChange={(event) => updateDraft((current) => ({ ...current, persona: { ...current.persona, tagline: event.target.value } }))}
                    />
                  </div>
                </div>
              )}
              {/* ストーリー */}
              {isEditing ? (
                <TaskyTextarea
                  value={draft.persona?.story || ''}
                  rows={3}
                  onChange={(event) => updateDraft((current) => ({ ...current, persona: { ...current.persona, story: event.target.value } }))}
                />
              ) : (
                <p className="rounded-xl bg-warm-surface p-3.5 text-[12.5px] leading-relaxed text-gray-600">
                  {draft.persona?.story || '-'}
                </p>
              )}

              {/* ペルソナ属性 6項目 */}
              <div className="mt-3.5 grid grid-cols-3 gap-x-4 gap-y-2.5">
                {DEMO_FIELDS.map(({ label, key }) => (
                  <div key={key}>
                    <div className="text-[10px] text-gray-400">{label}</div>
                    {isEditing ? (
                      <TaskyInput
                        value={draft.demo?.[key] || ''}
                        className="mt-0.5"
                        onChange={(event) => updateDraft((current) => ({ ...current, demo: { ...current.demo, [key]: event.target.value } }))}
                      />
                    ) : (
                      <div className="text-[12px] font-semibold text-gray-700">{draft.demo?.[key] || '—'}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* インサイト ▶ バリュー */}
              <div className="mt-4 flex items-stretch border-t border-warm-border pt-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[11.5px] font-bold text-gray-500">
                    <span className="material-icons-round text-[15px] text-pink">favorite</span>
                    インサイト（感情マップ）
                  </div>
                  <div className="flex flex-col gap-[11px]">
                    {INSIGHT_FIELDS.map(({ label, key }) => (
                      <div className="mt-0" key={key}>
                        <div className="text-[10.5px] font-bold text-gray-400">{label}</div>
                        {isEditing ? (
                          <TaskyTextarea
                            value={draft.emotion_map?.[key] || ''}
                            rows={2}
                            className="mt-0.5"
                            onChange={(event) => updateDraft((current) => ({ ...current, emotion_map: { ...current.emotion_map, [key]: event.target.value } }))}
                          />
                        ) : (
                          <div className="mt-0.5 text-[12.5px] leading-[1.55] text-gray-700">{draft.emotion_map?.[key] || '—'}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-[0_0_auto] items-center justify-center self-center px-3 text-[#c4c0b4]">
                  <span className="material-icons-round text-[22px]">play_arrow</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[11.5px] font-bold text-gray-500">
                    <span className="material-icons-round text-[15px]">diamond</span>
                    バリュー（ベネフィット）
                  </div>
                  <div>
                    {VALUE_ROWS.map(({ label, key, color, icon }) => (
                      <div key={key} className="flex items-start gap-2.5 border-b border-warm-surface py-2 last:border-0">
                        <span
                          className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg"
                          style={{ background: `${color}1A`, color }}
                        >
                          <span className="material-icons-round text-[14px]">{icon}</span>
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-bold" style={{ color }}>{label}</div>
                          {isEditing ? (
                            <TaskyTextarea
                              value={draft.value?.[key] || ''}
                              rows={2}
                              className="mt-0.5"
                              onChange={(event) => updateDraft((current) => ({ ...current, value: { ...current.value, [key]: event.target.value } }))}
                            />
                          ) : (
                            <div className="text-[13px] leading-relaxed text-gray-700">{draft.value?.[key] || '—'}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* ===== 訴求設計 ===== */}
          <div className="mt-4 flex-shrink-0">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="m-0 flex items-center gap-[7px] tracking-[0.01em] text-[12.5px] font-extrabold text-dark">
                <span className="material-icons-round text-[18px]">campaign</span>
                訴求設計
              </h3>
              <TaskyButton type="button" size="sm" variant="outline" onClick={handleAddAppeal}>
                <Plus className="h-4 w-4" />
                訴求を追加
              </TaskyButton>
            </div>
            <div className="space-y-3" ref={appealsListRef}>
              {draft.appeals.map((appeal) => (
                <AppealGenerateCard
                  key={appeal.appeal_id}
                  appeal={appeal}
                  selected={appeal.appeal_id === selectedAppeal?.appeal_id}
                  isEditing={isEditing}
                  regeneratingCopyIds={regeneratingCopyIds}
                  onSelect={() => setSelectedAppealId(appeal.appeal_id)}
                  onToggleCopy={handleToggleCopy}
                  onRegenerateUnusedCopies={handleRegenerateUnusedCopies}
                  onEditCopy={handleEditCopy}
                  onDeleteCopy={handleDeleteCopy}
                  onEditField={handleEditAppealField}
                  onAddCopy={handleAddCopy}
                  onDeleteAppeal={handleDeleteAppeal}
                />
              ))}
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-2xl border-[1.5px] border-dashed border-[#c7ccef] bg-transparent p-3 text-[12.5px] font-bold text-primary-hover transition-all duration-[120ms] hover:border-primary hover:bg-primary/[0.05]"
                onClick={handleAddAppeal}
              >
                <span className="material-icons-round text-[16px]">add</span>
                訴求を追加（AIが生成）
              </button>
            </div>
          </div>
        </div>

        {/* 訴求追加モーダル（AIにおまかせ／指示を出して生成） */}
        <AppealGenerateModal
          isOpen={isAppealModalOpen}
          productId={productId}
          personaName={draft.who_name}
          onClose={() => setIsAppealModalOpen(false)}
          onCreate={appendAppeal}
        />
      </aside>
    </div>
  );
};

export default PersonaDrawer;
